import {useDroppable} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import React from 'react';
import {useTranslation} from 'react-i18next';

import {DragIndicator} from '@ELib/assets/images';

import {
  ListElementWrappers,
  PlaceholderCenter,
  StyledList,
  StyledListItemButton,
} from './ColumnsCustomizeDialog.styles';
import {SortElement} from './ColumnsCustomizeDialog.types';

export const ColumnOptions = ({
  name,
  label,
  columnItems,
  selectedItemId,
  setSelectedItemId,
}: {
  name: string;
  label: string;
  columnItems?: SortElement[];
  selectedItemId: string | undefined;
  setSelectedItemId: (newSelectedId: string) => void;
}): React.ReactElement | null => {
  const {t} = useTranslation('lib');

  const {setNodeRef} = useDroppable({id: name});

  if (!columnItems) {
    return null;
  }

  return (
    <div>
      <h4>{label}</h4>
      <SortableContext
        id={name}
        items={columnItems}
        strategy={verticalListSortingStrategy}
      >
        <ListElementWrappers ref={setNodeRef}>
          <StyledList>
            {columnItems.map((columnItem) => (
              <SortableItem
                key={columnItem.id}
                id={columnItem.id}
                isSelected={columnItem.id === selectedItemId}
                translatedName={columnItem.label}
                onClick={() => setSelectedItemId(columnItem.id)}
              />
            ))}

            {columnItems.length < 1 ? (
              <PlaceholderCenter>
                {t('columnsDialog.placeholderItem')}
              </PlaceholderCenter>
            ) : undefined}
          </StyledList>
        </ListElementWrappers>
      </SortableContext>
    </div>
  );
};

const SortableItem = ({
  id,
  translatedName,
  onClick,
  isSelected,
}: {
  id: string;
  translatedName: string | React.ReactElement;
  onClick?: () => void;
  isSelected: boolean;
}) => {
  const {attributes, listeners, setNodeRef, transform, transition} =
    useSortable({id});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <StyledListItemButton
      ref={setNodeRef}
      data-testid='ETable-columnsDialog-listItem'
      selected={isSelected}
      style={style}
      {...attributes}
      {...listeners}
      className={isSelected ? 'active' : undefined}
      disableRipple
      onClick={onClick}
    >
      <DragIndicator />
      {translatedName}
    </StyledListItemButton>
  );
};
